import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from './ui/select';

const INITIAL_FORM = {
  name: '',
  address: '',
  description: '',
  beds: 1,
  baths: 1,
  type: 'apartment',
  hasTenant: false,
  tenantName: '',
  leaseEndDate: '',
};

const PropertyModal = ({ isOpen, onClose, onSave, property, saving }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  const isEditing = !!property;

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name || '',
        address: property.address || '',
        description: property.description || '',
        beds: property.beds ?? 1,
        baths: property.baths ?? 1,
        type: property.type || 'apartment',
        hasTenant: !!property.tenant,
        tenantName: property.tenant?.name || '',
        leaseEndDate: property.tenant?.leaseEndDate || '',
      });
    } else {
      setFormData(INITIAL_FORM);
    }
  }, [property, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      address: formData.address,
      description: formData.description,
      beds: formData.beds,
      baths: formData.baths,
      type: formData.type,
      tenant:
        formData.hasTenant && formData.tenantName
          ? { name: formData.tenantName, leaseEndDate: formData.leaseEndDate }
          : undefined,
    });
  };

  const update = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Property' : 'Add New Property'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the details for this property.'
              : 'Fill in the details to add a new property.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-2">
          <div className="space-y-2">
            <Label htmlFor="prop-name">Property Name</Label>
            <Input
              id="prop-name"
              required
              value={formData.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g., Sunset Villa"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-address">Address</Label>
            <Input
              id="prop-address"
              required
              value={formData.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="e.g., 123 Main St, Ames, IA 50014"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prop-description">Description</Label>
            <Input
              id="prop-description"
              value={formData.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Brief description of the property"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prop-beds">Bedrooms</Label>
              <Input
                id="prop-beds"
                type="number"
                min="0"
                value={formData.beds}
                onChange={(e) => update('beds', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-baths">Bathrooms</Label>
              <Input
                id="prop-baths"
                type="number"
                min="0"
                step="0.5"
                value={formData.baths}
                onChange={(e) => update('baths', parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="prop-type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(val) => update('type', val)}
              >
                <SelectTrigger id="prop-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="house">House</SelectItem>
                  <SelectItem value="ranch">Ranch</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="townhouse">Townhouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t border-[#E2E8F0] pt-6">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                id="hasTenant"
                checked={formData.hasTenant}
                onChange={(e) => update('hasTenant', e.target.checked)}
                className="w-4 h-4 rounded border-[#E2E8F0] text-[#16A34A] focus:ring-[#16A34A]"
              />
              <Label htmlFor="hasTenant">Property has a current tenant</Label>
            </div>

            {formData.hasTenant && (
              <div className="space-y-4 pl-7">
                <div className="space-y-2">
                  <Label htmlFor="tenantName">Tenant Name</Label>
                  <Input
                    id="tenantName"
                    value={formData.tenantName}
                    onChange={(e) => update('tenantName', e.target.value)}
                    placeholder="e.g., John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="leaseEnd">Lease End Date</Label>
                  <Input
                    id="leaseEnd"
                    type="date"
                    value={formData.leaseEndDate}
                    onChange={(e) => update('leaseEndDate', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            >
              {saving
                ? 'Saving…'
                : isEditing
                ? 'Save Changes'
                : 'Add Property'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyModal;
